import React from 'react';
import { InfoPanel, InfoPanelColumn } from '../../App/Components/InfoPanel';
import { observer } from 'mobx-react';
import { Neo } from '@singularsystems/neo-react';
import AccountInformationVM, { InfoPanelView } from './AccountInformationVM';
import AccountIdentityInfo, { AccountIdentityEditModal } from './Components/AccountIdentityInfo';
import AccountAddressInfo, { AccountAddressEditModal } from './Components/AccountAddressInfo';
import { BankDetailsInfo, ThirdPartyBankDetailsInfo, BankDetailsEditModal, ThirdPartyBankDetailsEditModal } from './Components/BankDetailsInfo';
import TaxInfo, { TaxDetailsEditModal } from './Components/TaxInfo';
import { PortfolioType } from '../../Models/Portfolio/PortfolioType';
import InvestorLookup from '../../Models/Investors/InvestorLookup';
import { EnumHelper } from '@singularsystems/neo-core';
import InvestmentInfo from './Components/InvestmentInfo';
import { PortfolioSign } from '../../Models/Registration/Portfolio';
import ClickLink from '../../App/Components/ClickLink';
import InvestorTypeSectionConfig from '../../Models/Investors/InvestorTypeSectionConfig';

interface IAccountInformationProps {
    viewModel: AccountInformationVM;
}

@observer
export default class AccountInformationPanel extends React.Component<IAccountInformationProps> {

    private selectView(view: InfoPanelView) {
        this.props.viewModel.selectedView = view;
        this.props.viewModel.panelController.forward("InvestorDetails");
    }

    public render() {
        const viewModel = this.props.viewModel;

        return (
            <>
                <InfoPanel controller={viewModel.panelController} arrowAnchorSelector="#user-settings-wrapper">
                    <div className="account-information-panel">
                        <InfoPanelColumn name="Home" backToText="Settings">
                            {this.renderHomeView()}
                        </InfoPanelColumn>

                        <InfoPanelColumn name="InvestorDetails" renderBackLink>
                            <Neo.Loader task={this.props.viewModel.taskRunner}>
                                {viewModel.investor &&
                                    <>
                                        {viewModel.selectedView === InfoPanelView.Identity && <AccountIdentityInfo viewModel={viewModel} />}
                                        {viewModel.selectedView === InfoPanelView.Address && <AccountAddressInfo viewModel={viewModel} />}
                                        {viewModel.selectedView === InfoPanelView.BankDetails && <BankDetailsInfo viewModel={viewModel} />}
                                        {viewModel.selectedView === InfoPanelView.ThirdPartyBankDetails && <ThirdPartyBankDetailsInfo viewModel={viewModel} />}
                                        {viewModel.selectedView === InfoPanelView.TaxDetails && <TaxInfo viewModel={viewModel} />}
                                    </>}
                            </Neo.Loader>
                        </InfoPanelColumn>

                        {viewModel.investmentVM &&
                            <InvestmentInfo viewModel={viewModel.investmentVM} />}

                        <InfoPanelColumn name="StartInvestment" renderBackLink>
                            <Neo.Loader task={this.props.viewModel.taskRunner}>
                                {viewModel.newInvestmentCommand && this.renderStartInvestment(viewModel.newInvestmentCommand)}
                            </Neo.Loader>
                        </InfoPanelColumn>
                    </div>
                </InfoPanel>

                <AccountIdentityEditModal viewModel={viewModel} />
                <AccountAddressEditModal viewModel={viewModel} />
                <BankDetailsEditModal viewModel={viewModel} />
                <ThirdPartyBankDetailsEditModal viewModel={viewModel} />
                <TaxDetailsEditModal viewModel={viewModel} />
            </>
        );
    }

    private renderHomeView() {
        const viewModel = this.props.viewModel;
        const sectionConfigs = viewModel.sectionConfigs;

        const sectionKeyToEnum: Record<string, number> = {
            Identity: InfoPanelView.Identity,
            ProofOfResidence: InfoPanelView.Address,
            BankDetails: InfoPanelView.BankDetails,
            TaxDetails: InfoPanelView.TaxDetails,
            ThirdPartyBank: InfoPanelView.ThirdPartyBankDetails
        };

        const renderSectionLink = (config: InvestorTypeSectionConfig) => {
            const enumValue = sectionKeyToEnum[config.sectionKey];
            if (enumValue === undefined) return null;
            return (
                <div key={config.sectionKey}>
                    {this.renderViewLink(config.displayLabel, enumValue)}
                </div>
            );
        };

        const visibleConfigs = sectionConfigs
            .filter(config => config.isVisible);

        return (
            <Neo.Loader task={viewModel.taskRunner}>
                <h4>Investor Details</h4>

                {visibleConfigs.filter(config => config.sectionKey === "Identity" || config.sectionKey === "ProofOfResidence").map(renderSectionLink)}

                <h4 className="mt-5">Investments & Debit Orders</h4>
                {viewModel.appInvestorData.investor &&
                    <>
                        {viewModel.allowedPortfolioTypes.map(pt =>
                            <React.Fragment key={pt}>
                                {this.renderInvestmentLink(viewModel.appInvestorData.investor!, pt)}
                            </React.Fragment>
                        )}
                    </>}

                <h4 className="mt-5">Other</h4>

                {visibleConfigs.filter(config => config.sectionKey === "TaxDetails" || config.sectionKey === "ThirdPartyBank" || config.sectionKey === "BankDetails").map(renderSectionLink)}
            </Neo.Loader>
        );
    }

    private renderStartInvestment(newInvestment: PortfolioSign) {
        const viewModel = this.props.viewModel,
            portfolioTypeName = EnumHelper.getItemMetadata(PortfolioType, newInvestment.portfolioTypeID).display;

        return (
            <div>
                <h4>Open {portfolioTypeName} Account</h4>

                <p>Please read and accept the investment mandate to open your {portfolioTypeName.toLowerCase()} investment account.</p>

                <ClickLink onClick={() => this.props.viewModel.documentApiClient.downloadMaintenanceDocument(newInvestment.maintenanceDocumentType, viewModel.taskRunner)}>
                    View full mandate
                </ClickLink>

                <Neo.Form model={newInvestment} onSubmit={() => viewModel.createInvestment()}>
                    <Neo.FormGroup className="mt-3" bind={newInvestment.meta.acceptTerms} label="I have read and agree to the investment mandate" />

                    <Neo.Button className="mt-1" isSubmit>Start Investing</Neo.Button>
                </Neo.Form>
            </div>

        );
    }

    private renderInvestmentLink(investor: InvestorLookup, portfolioType: PortfolioType) {
        const viewModel = this.props.viewModel,
            portfolio = investor.portfolioList.find(c => c.portfolioTypeID === portfolioType);

        const portfolioTypeName = EnumHelper.getItemMetadata(PortfolioType, portfolioType).display;

        return this.renderMainLink(
            portfolioTypeName,
            () => portfolio ? viewModel.selectPortfolio(portfolio) : viewModel.beginNewInvestment(portfolioType),
            portfolio ? undefined : "fa-plus",
            portfolio ? undefined : `Open ${portfolioTypeName} account`);
    }

    private renderViewLink(text: string, view: InfoPanelView, icon?: string, tooltip?: string) {
        return this.renderMainLink(text, () => this.selectView(view), icon, tooltip);
    }

    private renderMainLink(text: string, onClick: () => void, icon?: string, tooltip?: string) {
        return (
            <a className="link-with-arrow" href="/" data-tip={tooltip} data-tip-align="end" onClick={e => { e.preventDefault(); onClick(); }}>
                {text} <i className={"fa fa-fw " + (icon ?? "fa-angle-right")} />
            </a>
        );
    }
}